import { Button } from '@synoptic/ui-kit/button/button.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { LightBulbIcon } from '@synoptic/ui-kit/icons/react/light-bulb.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { Switch } from '@synoptic/ui-kit/switch/switch.js';
import { PropsWithChildren, ReactNode, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  CustomCollectionFilterConfig,
  Stream,
} from '@/__generated__/schema-types';
import { CollapsibleLabel } from '@/components/collapsible-label/collapsible-label';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal';
import { streamTiers } from '@/constants/stream-tiers';
import {
  ONBOARDING_TARGETS,
  onboardingTargetProps,
} from '@/routes/_app.terminal._index/terminal/onboarding/onboarding-targets';
import { useMe } from '@/routes/_app/use-me';
import { AIFilters } from '../ai-filters/ai-filters';
import { ColorPicker } from './color-picker';
import { COLORS } from './color-picker-colors.ts';
import {
  CommitStreamTargets,
  CustomTargetsSection,
  TargetAdditions,
} from './custom-targets-section/custom-targets-section';
import {
  hasSubscribedCustomStream,
  SourceCustomTargetsMap,
} from './custom-targets-section/source-custom-targets';
import {
  ActiveFiltersSummary,
  KeywordFiltersSummary,
  RegexFilterSummary,
} from './filters-summary/filters-summary';
import {
  REGEX_HINT_DESCRIPTION,
  REGEX_HINT_TITLE,
  RegexHelpAction,
} from './regex-help-hint.tsx';
import { isSmartNewsStream } from './stream-matching';
import { StreamSourceIdsMap } from './stream-sources-section/stream-source-ids';
import { StreamSourcesSection } from './stream-sources-section/stream-sources-section';
import { TypeaheadMultiselect } from './typeahead-multiselect/typeahead-multiselect';
import { useTier1Streams } from './typeahead-multiselect/use-tier-package-streams';
import { formFieldDescription } from '../ai-filters/manage-filter/manage-filter.css';
import {
  createBtn,
  listForm,
  regexInput,
  contentContainer,
  group,
  expandableGroup,
  expandableSwitchGroupLabel,
  divider,
  highlightValueContainer,
  highlightValueInputContainer,
  addHighlightButton,
  filterInput,
  sectionHeaderLabel,
  sectionRule,
  matchingSourcesHint,
  matchingSourcesGroup,
  matchingSourcesIcon,
  matchingSourcesTitle,
  incompatibleSourcesRow,
  sourcesField,
} from './list-form.css';

export type ListFormInputs = {
  name: string;
  sources: Stream[];
  /** Per-stream subset of custom targets; missing/empty entry = all targets */
  sourceCustomTargets?: SourceCustomTargetsMap;
  /** Per-stream subset of ingest sources; missing/empty entry = all sources */
  streamSourceIds?: StreamSourceIdsMap;
  filters: CustomCollectionFilterConfig;
  highlights?: {
    isEnabled: boolean;
    values: Array<{ regex: string; color: string }>;
  } | null;
  /** Restricts sources to streams with market matching or in Tier 1. */
  matchingSourcesOnly?: boolean;
};

interface ListFormProps {
  initialValues?: ListFormInputs;
  submit: (form: ListFormInputs) => void;
  isSubmitLoading?: boolean;
  submitText?: string;
  /** A list already narrowed by its widget leads with everything else. */
  filtersLast?: boolean;
  collectionId?: string;
  /** Owner-supplied controls for what the highlights do in that list. */
  afterHighlights?: (api: { enableHighlights: () => void }) => ReactNode;
  /** The server owns the list's streams. */
  sourcesLocked?: boolean;
  /** Where a new list leads with matching; an existing one keeps its own. */
  defaultMatchingSources?: boolean;
}

const MAX_SOURCES = 100;

function parseUserRegex(userInput: string) {
  const match = userInput.match(/^\/(.+)\/([a-z]*)$/i);

  if (!match) {
    throw new Error();
  }

  const pattern = match[1];
  const flags = match[2];

  return new RegExp(pattern, flags);
}

const filterValidation = {
  maxLength: {
    value: 1000,
    message: 'Filter cannot exceed 1000 characters',
  },
};

export const ListForm = ({
  initialValues,
  submit,
  isSubmitLoading,
  submitText = 'Create',
  filtersLast = false,
  collectionId,
  afterHighlights,
  sourcesLocked = false,
  defaultMatchingSources = false,
  children,
}: PropsWithChildren<ListFormProps>) => {
  const isTablet = useIsTablet();

  const defaultMatchingSourcesOnly =
    initialValues?.matchingSourcesOnly ?? defaultMatchingSources;
  // Read once: flipping the switch later must not move a section the user set.
  const [areFiltersOpenByDefault] = useState(!defaultMatchingSourcesOnly);
  const me = useMe();
  const commitStreamTargets = useRef<CommitStreamTargets | null>(null);
  // Added targets ask about the lists pinned to that stream, one at a time.
  const [pendingAdditions, setPendingAdditions] = useState<TargetAdditions[]>(
    [],
  );
  const [savedValues, setSavedValues] = useState<ListFormInputs | null>(null);

  const saveList = (formValues: ListFormInputs) =>
    submit({
      ...formValues,
      highlights: {
        isEnabled: formValues.highlights?.isEnabled || false,
        values:
          formValues.highlights?.values?.filter(
            (value) => value.regex.trim() !== '',
          ) || [],
      },
    });

  const onAdditionsHandled = () => {
    const remaining = pendingAdditions.slice(1);
    setPendingAdditions(remaining);
    if (remaining.length === 0 && savedValues) {
      saveList(savedValues);
      setSavedValues(null);
    }
  };

  const initialFilters = initialValues?.filters || {};

  const initialHighlightValues =
    initialValues?.highlights?.values &&
    initialValues?.highlights?.values?.length > 0
      ? initialValues?.highlights?.values?.map((value) => ({
          regex: value.regex,
          color: value.color,
        }))
      : [
          {
            regex: '',
            color: 'green',
          },
        ];

  const { handleSubmit, formState, register, control, watch, setValue } =
    useForm<ListFormInputs>({
      mode: 'onChange',
      shouldFocusError: true,
      defaultValues: {
        name: initialValues?.name || '',
        sources: initialValues?.sources || [],
        sourceCustomTargets: initialValues?.sourceCustomTargets || {},
        streamSourceIds: initialValues?.streamSourceIds || {},
        filters: {
          includeKeywords: {
            all: initialFilters.includeKeywords?.all || '',
            any: initialFilters.includeKeywords?.any || '',
            phrase: initialFilters.includeKeywords?.phrase || '',
          },
          excludeKeywords: {
            all: initialFilters.excludeKeywords?.all || '',
            any: initialFilters.excludeKeywords?.any || '',
            phrase: initialFilters.excludeKeywords?.phrase || '',
          },
          ai: initialFilters.ai || null,
          regex: initialFilters.regex || '',
          isHighlightEnabled: !!initialFilters.isHighlightEnabled,
          highlightColor: initialFilters.highlightColor || 'lightYellow',
        },
        highlights: {
          isEnabled: initialValues?.highlights?.isEnabled || false,
          values: initialHighlightValues,
        },
        matchingSourcesOnly: defaultMatchingSourcesOnly,
      },
    });

  const {
    fields: highlightFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'highlights.values',
  });

  const selectedSources = watch('sources');
  const hasCustomTargets = hasSubscribedCustomStream(selectedSources, me.id);
  const matchingSourcesOnly = watch('matchingSourcesOnly');

  const { ids: tier1StreamIds, isLoading: tier1Loading } = useTier1Streams();

  // Flipping the switch on can strand streams the list already had. They are
  // flagged rather than dropped: losing a source on save is not recoverable,
  // and turning the switch back off is a valid answer too.
  const incompatibleSources =
    matchingSourcesOnly && !tier1Loading
      ? selectedSources.filter(
          (source) => !isSmartNewsStream(source, tier1StreamIds),
        )
      : [];

  const filterSections = (
    <>
      <CollapsibleLabel
        title="Include filters"
        summary={
          <KeywordFiltersSummary
            control={control}
            name="filters.includeKeywords"
          />
        }
        testId={'include-filters'}
      >
        <Input
          label="All of these words"
          placeholder='Example: earnings forecast — include posts with "earnings" and "forecast"'
          errorMessage={formState.errors.filters?.includeKeywords?.all?.message}
          className={filterInput}
          {...register('filters.includeKeywords.all', filterValidation)}
        />
        <Input
          label="Any of these words"
          placeholder='Example: SPY QQQ — include posts with "SPY" or "QQQ" (or both)'
          errorMessage={formState.errors.filters?.includeKeywords?.any?.message}
          className={filterInput}
          {...register('filters.includeKeywords.any', filterValidation)}
        />
        <Input
          label="This exact phrase"
          placeholder='Example: buy the dip — include all posts with this phrase "buy the dip"'
          errorMessage={
            formState.errors.filters?.includeKeywords?.phrase?.message
          }
          className={filterInput}
          {...register('filters.includeKeywords.phrase', filterValidation)}
        />
      </CollapsibleLabel>
      <CollapsibleLabel
        title="Exclude filters"
        summary={
          <KeywordFiltersSummary
            control={control}
            name="filters.excludeKeywords"
          />
        }
        testId={'exclude-filters'}
      >
        <Input
          label="All of these words"
          placeholder='Example: crypto NFT — exclude posts with "crypto" and "NFT"'
          errorMessage={formState.errors.filters?.excludeKeywords?.all?.message}
          className={filterInput}
          {...register('filters.excludeKeywords.all', filterValidation)}
        />
        <Input
          label="Any of these words"
          placeholder='Example: rumor leak — exclude posts with "rumor" or "leak"'
          errorMessage={formState.errors.filters?.excludeKeywords?.any?.message}
          className={filterInput}
          {...register('filters.excludeKeywords.any', filterValidation)}
        />
        <Input
          label="This exact phrase"
          placeholder='Example: financial advisor — exclude all posts containing this phrase "financial advisor"'
          errorMessage={
            formState.errors.filters?.excludeKeywords?.phrase?.message
          }
          className={filterInput}
          {...register('filters.excludeKeywords.phrase', filterValidation)}
        />
      </CollapsibleLabel>
      <CollapsibleLabel
        title="Regex"
        description="advanced"
        summary={<RegexFilterSummary control={control} />}
      >
        <>
          <Input
            label="Enter a regex pattern"
            hintTitle={REGEX_HINT_TITLE}
            hint={REGEX_HINT_DESCRIPTION}
            hintAction={<RegexHelpAction />}
            placeholder="ex: /[A-Z]\w+/"
            containerClassName={regexInput}
            errorMessage={formState.errors.filters?.regex?.message}
            {...register('filters.regex', {
              validate: (regexString) => {
                if (!regexString) {
                  return true;
                }
                try {
                  parseUserRegex(regexString);
                  return true;
                } catch {
                  return 'Invalid regex pattern. Please check the syntax.';
                }
              },
            })}
          />
          {!formState.errors.filters?.regex && (
            <p className={formFieldDescription}>
              Use regex to filter results based on patterns.
            </p>
          )}
        </>
      </CollapsibleLabel>
      <Controller
        name="filters.ai"
        control={control}
        render={({ field }) => (
          <AIFilters
            onSelectFilter={field.onChange}
            selectedFilter={field.value}
          />
        )}
      />
      <Controller
        name="filters.isHighlightEnabled"
        control={control}
        render={({ field }) => (
          <Collapsible
            open={field.value || false}
            data-testid="highlight filter results"
          >
            <CollapsibleTrigger asChild>
              <label className={expandableSwitchGroupLabel}>
                Highlight filter results
                <Switch
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                />
              </label>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={expandableGroup}>
                <Controller
                  name="filters.highlightColor"
                  control={control}
                  render={({ field }) => (
                    <ColorPicker
                      label="Highlight color"
                      selectedColor={field.value}
                      onColorSelect={field.onChange}
                    />
                  )}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      />
    </>
  );

  const filtersGroup = (
    <div
      className={group}
      {...onboardingTargetProps(ONBOARDING_TARGETS.editListFilters)}
    >
      <CollapsibleLabel
        title="Filters"
        className={sectionHeaderLabel}
        defaultOpen={areFiltersOpenByDefault}
        summary={<ActiveFiltersSummary control={control} />}
        testId="filters"
      >
        <div className={sectionRule} />
        <div className={contentContainer}>{filterSections}</div>
      </CollapsibleLabel>
    </div>
  );

  return (
    <form
      className={listForm}
      onSubmit={handleSubmit(async (formValues) => {
        // Targets belong to the stream, so they go first: a list naming one
        // the stream never got is rejected.
        const targets = await commitStreamTargets.current?.();
        if (targets && !targets.ok) {
          return;
        }
        if (targets?.additions.length) {
          setSavedValues(formValues);
          setPendingAdditions(targets.additions);
          return;
        }
        saveList(formValues);
      })}
    >
      <Input
        label="Name your list"
        placeholder="ex. world news"
        errorMessage={formState.errors.name?.message}
        type="text"
        {...register('name', {
          required: 'This field is required',
          maxLength: {
            value: 30,
            message: 'Name cannot exceed 30 characters',
          },
          validate: (v) => v.trim().length > 0,
        })}
        autoFocus
      />
      <Controller
        name="sources"
        control={control}
        rules={{
          required: !sourcesLocked && 'This field is required',
          validate: (value) => {
            return value.length > MAX_SOURCES
              ? `You can only add up to ${MAX_SOURCES} streams to a list`
              : true;
          },
        }}
        render={({ field }) => {
          if (sourcesLocked) return <></>;
          return (
            <div className={sourcesField}>
              <TypeaheadMultiselect
                selectedSources={field.value}
                onSourcesChange={field.onChange}
                errorMessage={formState.errors.sources?.message}
                matchingSourcesOnly={!!matchingSourcesOnly}
              />
              {incompatibleSources.length > 0 ? (
                <div className={incompatibleSourcesRow}>
                  <span>
                    {incompatibleSources.length}{' '}
                    {incompatibleSources.length === 1 ? 'stream' : 'streams'} in
                    this list{' '}
                    {incompatibleSources.length === 1 ? 'has' : 'have'} no
                    market matching and{' '}
                    {incompatibleSources.length === 1 ? 'is' : 'are'} not in{' '}
                    {streamTiers.tier1.name}.
                  </span>
                  <LinkButton
                    size="small"
                    onClick={() =>
                      field.onChange(
                        field.value.filter((source) =>
                          isSmartNewsStream(source, tier1StreamIds),
                        ),
                      )
                    }
                  >
                    Remove {incompatibleSources.length === 1 ? 'it' : 'them'}
                  </LinkButton>
                </div>
              ) : null}
            </div>
          );
        }}
      />
      {sourcesLocked ? null : (
        <Controller
          name="matchingSourcesOnly"
          control={control}
          render={({ field }) => (
            <div
              className={matchingSourcesGroup}
              // The tour rings the targets field, and falls back to here for a
              // list that has no custom stream to show one.
              {...(hasCustomTargets
                ? {}
                : onboardingTargetProps(ONBOARDING_TARGETS.editListHighlight))}
            >
              <label className={expandableSwitchGroupLabel}>
                <span className={matchingSourcesTitle}>
                  <LightBulbIcon className={matchingSourcesIcon} />
                  Smart news only
                </span>
                <Switch
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                  aria-label="Smart news only"
                />
              </label>
              <p className={matchingSourcesHint}>
                {`Streams with market matching and all ${streamTiers.tier1.name} streams. Disable to add any stream.`}
              </p>
            </div>
          )}
        />
      )}
      <Controller
        name="sourceCustomTargets"
        control={control}
        render={({ field }) => (
          <CustomTargetsSection
            sources={selectedSources}
            value={field.value || {}}
            onChange={field.onChange}
            matchingSourcesOnly={!!matchingSourcesOnly}
            collectionId={collectionId}
            commitRef={commitStreamTargets}
          />
        )}
      />
      <Controller
        name="streamSourceIds"
        control={control}
        render={({ field }) => (
          <StreamSourcesSection
            sources={selectedSources}
            value={field.value || {}}
            onChange={field.onChange}
          />
        )}
      />
      {filtersLast ? null : filtersGroup}

      <Controller
        name="highlights.isEnabled"
        control={control}
        render={({ field }) => (
          <Collapsible
            open={field.value || false}
            data-testid="highlight text"
            className={group}
          >
            <CollapsibleTrigger asChild>
              <label className={expandableSwitchGroupLabel}>
                Highlight text
                <Switch
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                />
              </label>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={expandableGroup}>
                {highlightFields.map((highlightField, index) => (
                  <div key={highlightField.id}>
                    {index > 0 && <div className={divider} />}
                    <div className={highlightValueContainer}>
                      <div className={highlightValueInputContainer}>
                        <Input
                          label="Enter a highlight regex pattern"
                          hintTitle={REGEX_HINT_TITLE}
                          hint={REGEX_HINT_DESCRIPTION}
                          hintAction={<RegexHelpAction />}
                          placeholder="ex: /[A-Z]\w+/"
                          errorMessage={
                            formState.errors?.highlights?.values?.[index]?.regex
                              ?.message
                          }
                          {...register(`highlights.values.${index}.regex`, {
                            validate: (regexString) => {
                              if (!field.value) {
                                return true;
                              }
                              if (!regexString) {
                                return 'This field is required';
                              }
                              try {
                                parseUserRegex(regexString);
                                return true;
                              } catch {
                                return 'Invalid regex pattern. Please check the syntax.';
                              }
                            },
                          })}
                        />
                      </div>
                      {highlightFields.length > 1 && (
                        <IconButton
                          variant="ghost"
                          size="small"
                          aria-label="Remove highlight"
                          onClick={() => remove(index)}
                        >
                          <TrashIcon />
                        </IconButton>
                      )}
                    </div>
                    <Controller
                      name={`highlights.values.${index}.color`}
                      control={control}
                      render={({ field }) => (
                        <ColorPicker
                          label="Highlight color"
                          selectedColor={field.value}
                          onColorSelect={field.onChange}
                        />
                      )}
                    />
                  </div>
                ))}
                {highlightFields.length < COLORS.length && (
                  <Button
                    variant="ghost"
                    size="small"
                    className={addHighlightButton}
                    onClick={() => append({ regex: '', color: 'green' })}
                    startIcon={<PlusIcon />}
                  >
                    Add highlight
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      />
      {afterHighlights?.({
        enableHighlights: () =>
          setValue('highlights.isEnabled', true, { shouldDirty: true }),
      })}
      {filtersLast ? filtersGroup : null}
      {children}
      <Button
        loading={isSubmitLoading || formState.isSubmitting}
        className={createBtn}
        disabled={
          Boolean(formState.errors.name) ||
          Boolean(formState.errors.sources) ||
          incompatibleSources.length > 0
        }
        type="submit"
        size={isTablet ? 'large' : 'medium'}
      >
        {submitText}
      </Button>
      {pendingAdditions[0] ? (
        <TargetsModal
          key={pendingAdditions[0].streamId}
          streamId={pendingAdditions[0].streamId}
          open
          onOpenChange={(next) => {
            if (!next) onAdditionsHandled();
          }}
          defaultView="addToLists"
          initialAddedTargets={pendingAdditions[0].targets}
          excludeCollectionId={collectionId}
          maxTargets={0}
        />
      ) : null}
    </form>
  );
};
